/**
 * The "Customer support" agent_template's product constants — plan-018 DOM, design §30.6.24 (ADRs 0058–0061).
 *
 * Budgets and fixed sizes of the information index, the website reading, the per-turn information block, the lexical
 * query and the warm handoff. They are PRODUCT constants: a number the product promises (Q2's 100 pages) or a size the
 * design fixed, never configuration and never measured. The configured retrieval sizes (`packages/config`) are
 * validated against them (results ≤ {@link INFORMATION_BLOCK_PASSAGE_LIMIT}); the measured floors live in
 * `packages/measured-constants` and raise while provisional.
 */
// ── the website reading (ADR 0059, Q2) ──────────────────────────────────────────────────────
/** At most this many same-site pages are read in one reading (owner answer Q2). */
export const WEBSITE_PAGE_LIMIT = 100;
/** Links are followed at most this deep from the start page (the plan's default, §0.2). */
export const WEBSITE_DEPTH_LIMIT = 3;
/** At most this many redirects per page, each re-screened. */
export const WEBSITE_REDIRECT_LIMIT = 5;
/** robots.txt is read up to this many bytes (RFC 9309 asks for at least 500 KiB). */
export const ROBOTS_BYTE_LIMIT = 524_288;
/** A start page with fewer visible characters than this beside scripts is "script only". */
export const SCRIPT_ONLY_CHARACTER_LIMIT = 200;
/** A website address is at most this many characters (design §30.6.1). */
export const WEBSITE_ADDRESS_CHARACTER_LIMIT = 2_048;
// ── the information index (ADR 0058) ────────────────────────────────────────────────────────
/** An information index holds at most this many passages; more fails the build (`information_too_large`). */
export const INFORMATION_PASSAGE_LIMIT = 6_000;
/** The chunker aims at this many embedder tokens per passage. */
export const PASSAGE_TARGET_TOKEN = 320;
/** A passage never exceeds this many embedder tokens (the embedding model's useful window). */
export const PASSAGE_MAX_TOKEN = 480;
/** A passage shorter than this is merged with its neighbour in the same section. */
export const PASSAGE_MIN_TOKEN = 24;
/** Overlap carried into the next passage, only when a section is split. */
export const PASSAGE_OVERLAP_TOKEN = 48;
/** The pasted text is at most this many bytes (`413 information_input_too_large`). */
export const PASTED_TEXT_BYTE_LIMIT = 524_288;
/** At most this many uploaded documents (the field's `maxFileCount`). */
export const INFORMATION_DOCUMENT_FILE_LIMIT = 10;
/** The chunker's revision, recorded on every index (`information_index.chunker_revision`). */
export const CHUNKER_REVISION = 1;
/** The embedding's dimension (`information_index.embedding_dimension`, `halfvec(384)`). */
export const EMBEDDING_DIMENSION = 384;
// ── the information card (OD-1) ─────────────────────────────────────────────────────────────
/** Blocks [2] and [4] rendered from the card are at most this many tokens per language. */
export const INFORMATION_CARD_TOKEN_LIMIT = 1_500;
/** OD-1, the owner's default (2026-10-13 AP-0, "Build with defaults"): the card is offered. `false` empties it. */
export const INFORMATION_CARD_OFFERED = true;
/** The card's description is one verbatim sentence of at most this many characters. */
export const INFORMATION_CARD_DESCRIPTION_CHARACTER_LIMIT = 300;
/** The card's stated hours, as written, at most this many characters. */
export const INFORMATION_CARD_STATED_HOURS_CHARACTER_LIMIT = 400;
/** At most this many phones and this many emails on the card. */
export const INFORMATION_CARD_CONTACT_LIMIT = 3;
// ── one caller turn (ADR 0058 decisions 6–7) ────────────────────────────────────────────────
/** At most this many retrieved passages go into one information block. */
export const INFORMATION_BLOCK_PASSAGE_LIMIT = 5;
/** …and at most this many embedder tokens of them. */
export const INFORMATION_BLOCK_TOKEN_LIMIT = 1_800;
/** A last caller utterance with fewer search tokens than this also searches with the previous one (a follow-up). */
export const FOLLOW_UP_TOKEN_LIMIT = 4;
/** The lexical OR query carries at most this many tokens. */
export const LEXICAL_TOKEN_LIMIT = 24;
/** The query embedding reads at most this many embedder tokens of the caller's words. */
export const QUERY_TOKEN_LIMIT = 64;
/**
 * The information block's text when no returned passage clears the floors (outcome `no_match`, design §30.6.15).
 * Rendered between the block's delimiters; on it the VoiceAgent says so, offers a message and records a gap (Q5).
 */
export const NO_MATCH_MARKER = "NO MATCHING INFORMATION for this question.";
// ── the warm handoff and taken messages (ADRs 0060, 0061) ───────────────────────────────────
/** At most this many handoff attempts in one conversation (`conversation_attempt_limit`). */
export const HANDOFF_ATTEMPT_PER_CONVERSATION = 2;
/** The handoff summary is at most about this many output tokens. */
export const HANDOFF_SUMMARY_TOKEN_LIMIT = 60;
/** The portal's derived notice feed shows taken messages from the last this-many days. */
export const TAKEN_MESSAGE_NOTICE_WINDOW_DAY = 14;
//# sourceMappingURL=limit.js.map